import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { Link, useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import * as XLSX from "xlsx";
import {
  getCategoryService,
  getUserCsvService,
  getUserListFilterListService,
  getUserListPageService,
} from "../../../services/ShinryuService";
import { cateList, getLocalStorage } from "../../../store/recoil";
import PageTitle from "../../layouts/PageTitle";
const PAGE_SIZE = 50;

function useSessionStorageState(key, defaultValue) {
  const [state, setState] = useState(
    () => sessionStorage.getItem(key) || defaultValue
  );
  useEffect(() => { sessionStorage.setItem(key, state); }, [key, state]);
  return [state, setState];
}

async function loadListMetadata() {
  const filters = await getUserListFilterListService({ silent: true });
  if (!filters || typeof filters !== "object" || Array.isArray(filters)) {
    throw new Error("Invalid filter response");
  }
  const categories = await getCategoryService();
  if (!Array.isArray(categories)) throw new Error("Invalid category response");
  return categories.filter((category) => category.status !== "INACTIVE");
}

const UserList = () => {
  const history = useHistory();
  const [reloadCount, setReloadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  // A page is local screen state; it must never replace the full-list Recoil atom.
  const [result, setResult] = useState({ items: [], total: 0, page: 1 });
  const metadataRequest = useRef(null);
  const [catlist, setCateList] = useRecoilState(cateList);
  const [globalFilter, setGlobalFilter] = useSessionStorageState("globalFilter", "");
  const [query, setQuery] = useSessionStorageState("query", "");
  const [query_employmentStartAt, setQuery_employmentStartAt] =
    useSessionStorageState("query_employmentStartAt", "");
  const [query_employmentEndAt, setQuery_employmentEndAt] =
    useSessionStorageState("query_employmentEndAt", "");
  const [selectedBelongTo, setSelectedBelongTo] = useSessionStorageState("selectedBelongTo", "");
  const [selectedGroup, setSelectedGroup] = useSessionStorageState("selectedGroup", "");
  const [selectedInsuranceType, setSelectedInsuranceType] = useSessionStorageState("selectedInsuranceType", "");
  const [selectedenrollmentType, setSelectedenrollmentType] = useSessionStorageState("selectedenrollmentType", "");
  const [selectedworkScope, setSelectedworkScope] = useSessionStorageState("selectedworkScope", "");
  const [selectedworkerType, setSelectedworkerType] = useSessionStorageState("selectedworkerType", "");
  const [searchTerms, setSearchTerms] = useState({ query, keyword: globalFilter });
  const [sort, setSort] = useState({ field: "userNumber", order: "DESC" });
  const [pagePosition, setPagePosition] = useState({ criteriaKey: "", page: 1 });
  const [csv, setCsvList] = useState(null);
  const [admin, setAdmin] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerms({ query, keyword: globalFilter });
    }, 300);
    return () => clearTimeout(timer);
  }, [query, globalFilter]);

  const criteria = useMemo(() => ({
    query: searchTerms.query,
    keyword: searchTerms.keyword,
    filters: {
      belongTo: selectedBelongTo,
      insuranceType: selectedInsuranceType,
      employmentType: "",
      userGroup: selectedGroup,
      enrollmentType: selectedenrollmentType,
      workScope: selectedworkScope,
      workerType: selectedworkerType,
      employmentStartAt: query_employmentStartAt,
      employmentEndAt: query_employmentEndAt,
    },
    sortField: sort.field,
    sortOrder: sort.order,
  }), [
    searchTerms.query, searchTerms.keyword, selectedBelongTo, selectedInsuranceType,
    selectedGroup, selectedenrollmentType, selectedworkScope, selectedworkerType,
    query_employmentStartAt, query_employmentEndAt, sort.field, sort.order,
  ]);
  const criteriaKey = JSON.stringify(criteria);
  // Derive page 1 synchronously for changed criteria, avoiding a second reset fetch.
  const currentPage = pagePosition.criteriaKey === criteriaKey ? pagePosition.page : 1;
  const request = useMemo(() => ({ ...criteria, page: currentPage, pageSize: PAGE_SIZE }), [criteria, currentPage]);
  useEffect(() => {
    setPagePosition((position) => position.criteriaKey === criteriaKey
      ? position
      : { criteriaKey, page: 1 });
  }, [criteriaKey]);

  useEffect(() => {
    let current = true;
    getLocalStorage("admininfo")
      .then((value) => { if (current) setAdmin(value); })
      .catch(() => {});
    return () => { current = false; };
  }, []);

  const onSearchChange = (event) => setGlobalFilter(event.target.value);
  const applySearchNow = () => setSearchTerms({ query, keyword: globalFilter });

  const downloadExcel = (csv) => {
    const worksheet = XLSX.utils.json_to_sheet(csv);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    //let buffer = XLSX.write(workbook, { bookType: "xlsx", type: "buffer" });
    //XLSX.write(workbook, { bookType: "xlsx", type: "binary" });
    XLSX.writeFile(workbook, "DataSheet.xlsx");
  };

  useEffect(() => {
    let current = true;
    setIsLoading(true);

    const loadPage = async () => {
      try {
        if (!metadataRequest.current) {
          metadataRequest.current = loadListMetadata().catch((error) => {
            metadataRequest.current = null;
            throw error;
          });
        }
        const categories = await metadataRequest.current;
        if (!current) return;
        setCateList(categories);

        const page = await getUserListPageService(request);
        if (!current) return;
        if (!page || !Array.isArray(page.items) || page.items.length > PAGE_SIZE ||
            !Number.isInteger(page.total) || page.total < page.items.length ||
            page.page !== request.page || page.pageSize !== PAGE_SIZE) {
          throw new Error("Invalid user page response");
        }
        setResult(page);
        setHasLoadError(false);
      } catch {
        if (current) setHasLoadError(true);
      } finally {
        if (current) setIsLoading(false);
      }
    };

    loadPage();
    return () => { current = false; };
  }, [request, reloadCount, setCateList]);

  const handleReset = () => {
    setGlobalFilter("");
    setQuery("");
    setSearchTerms({ query: "", keyword: "" });
    setQuery_employmentStartAt("");
    setQuery_employmentEndAt("");
    setSelectedGroup("");
    setSelectedBelongTo("");
    setSelectedInsuranceType("");
    setSelectedworkerType("");
    setSelectedworkScope("");
    setSelectedenrollmentType("");
    setPagePosition({ criteriaKey: "", page: 1 });
  };

  const handleCsv = async (admin) => {
    const CsvList = await getUserCsvService(admin);
    console.log("CsvList", CsvList);
    setCsvList(CsvList);
    let newList = [];
    for (let i = 0; i < CsvList.list.length; i++) {
      newList.push({
        id: CsvList.list[i].id,
        メールアドレス: CsvList.list[i].userEmail,
        ログインID: CsvList.list[i].userUniqueId,
        パスワード: CsvList.list[i].userPassword,
        隊員番号: CsvList.list[i].userNumber,
        裏番号: CsvList.list[i].userLastNumber,
        名前: CsvList.list[i].userName,
        フリガナ: CsvList.list[i].userNameHurigana,
        性別: CsvList.list[i].sex,
        生年月日: CsvList.list[i].userBirth,
        郵便番号: CsvList.list[i].postCode,
        住所01: CsvList.list[i].address01,
        住所02: CsvList.list[i].address02,
        住所03: CsvList.list[i].address03,
        電話番号: CsvList.list[i].userHousePhone,
        携帯電話: CsvList.list[i].userPhone,
        FAX番号: CsvList.list[i].userFaxNumber,
        所属: CsvList.list[i].belongTo,
        保険区分: CsvList.list[i].insuranceType,
        社員区分: CsvList.list[i].workerType,
        業務区分: CsvList.list[i].workScope,
        在籍区分: CsvList.list[i].enrollmentType,
        グループ: CsvList.list[i].userGroup,
        雇用期間_始: CsvList.list[i].employmentStartAt,
        雇用期間_終: CsvList.list[i].employmentEndAt,
        緊急連絡先関係: CsvList.list[i].emergencyContactRelation,
        緊急連絡先名前: CsvList.list[i].emergencyContactName,
        緊急連絡先住所: CsvList.list[i].emergencyContactAddress,
        緊急連絡先電話番号: CsvList.list[i].emergencyContactPhone,
        権限: CsvList.list[i].authority,
        メモ: CsvList.list[i].userMemo,
      });
    }
    downloadExcel(newList);
  };

  const handleButtonClick = (id) => {
    history.push(`/shinryu-userdetail?id=${id}`);
  };
  const handleButtonClick2 = (id) => {
    history.push(`/shinryu-userdetail?id=${id}&key=message`);
  };

  const first = (result.page - 1) * PAGE_SIZE;
  const startIndex = result.items.length ? first + 1 : 0;
  const endIndex = result.items.length ? first + result.items.length : 0;

  return (
    <>
      <div className="custom-row">
        <PageTitle activeMenu="社員管理" motherMenu="shinryu" />
        <div className="custom-header">
          <div
            style={
              admin === "管理者C" || admin === "管理者B"
                ? { display: "none" }
                : { display: "block" }
            }>
            <Button
              className="btn btn-primary rounded btn-sm"
              onClick={() => handleCsv(csv)}>
              Excelダウンロード
            </Button>
          </div>
          <div
            style={
              admin == "管理者C" ? { display: "none" } : { display: "block" }
            }>
            <Link to="/shinryu-adduser">
              <Button className="btn btn-primary rounded btn-sm" type="submit">
                新規登録
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <div style={{ display: "block", width: "100%" }}>
                <div className="form-head d-flex mb-3 align-items-center ">
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className={`form-control col-02 mr-3 ${
                      selectedGroup ? "" : "default-option"
                    }`}>
                    <option value="">グループ</option>
                    {catlist
                      .filter((cat) => cat.categoryBigClassName === "5") // filter by categoryBigClassName
                      .map((cat) => (
                        <option key={cat.id} value={cat.categorySmallClassName}>
                          {cat.categorySmallClassName}
                        </option>
                      ))}
                  </select>
                  <select
                    value={selectedBelongTo}
                    onChange={(e) => setSelectedBelongTo(e.target.value)}
                    className={`form-control col-02 mr-3 ${
                      selectedBelongTo ? "" : "default-option"
                    }`}>
                    <option value="">所属</option>
                    {catlist
                      .filter((cat) => cat.categoryBigClassName === "4")
                      .map((cat) => (
                        <option key={cat.id} value={cat.categorySmallClassName}>
                          {cat.categorySmallClassName}
                        </option>
                      ))}
                  </select>

                  <select
                    value={selectedInsuranceType}
                    onChange={(e) => setSelectedInsuranceType(e.target.value)}
                    className={`form-control col-02 mr-3 ${
                      selectedInsuranceType ? "" : "default-option"
                    }`}>
                    <option value="">保険区分</option>

                    {catlist
                      .filter((cat) => cat.categoryBigClassName === "6") // filter by categoryBigClassName
                      .map((cat) => (
                        <option key={cat.id} value={cat.categorySmallClassName}>
                          {cat.categorySmallClassName}
                        </option>
                      ))}
                  </select>

                  <select
                    value={selectedworkerType}
                    onChange={(e) => setSelectedworkerType(e.target.value)}
                    className={`form-control col-02 mr-3 ${
                      selectedworkerType ? "" : "default-option"
                    }`}>
                    <option value="">社員区分</option>

                    {catlist
                      .filter((cat) => cat.categoryBigClassName === "7") // filter by categoryBigClassName
                      .map((cat) => (
                        <option key={cat.id} value={cat.categorySmallClassName}>
                          {cat.categorySmallClassName}
                        </option>
                      ))}
                  </select>
                  <select
                    value={selectedworkScope}
                    onChange={(e) => setSelectedworkScope(e.target.value)}
                    className={`form-control col-02 mr-3 ${
                      selectedworkScope ? "" : "default-option"
                    }`}>
                    <option value="">業務区分</option>

                    {catlist
                      .filter((cat) => cat.categoryBigClassName === "8") // filter by categoryBigClassName
                      .map((cat) => (
                        <option key={cat.id} value={cat.categorySmallClassName}>
                          {cat.categorySmallClassName}
                        </option>
                      ))}
                  </select>
                  <select
                    value={selectedenrollmentType}
                    onChange={(e) => setSelectedenrollmentType(e.target.value)}
                    className={`form-control col-02 mr-3 ${
                      selectedenrollmentType ? "" : "default-option"
                    }`}>
                    <option value="">在籍区分</option>

                    {catlist
                      .filter((cat) => cat.categoryBigClassName === "9") // filter by categoryBigClassName
                      .map((cat) => (
                        <option key={cat.id} value={cat.categorySmallClassName}>
                          {cat.categorySmallClassName}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form-head d-flex mb-2 align-items-center ">
                  <div className="input-group search-area rounded d-inline-flex mr-sm-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="雇用期間開始日検索"
                      value={query_employmentStartAt}
                      onChange={(e) =>
                        setQuery_employmentStartAt(e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          applySearchNow();
                        }
                      }}
                    />
                    <div
                      className="input-group-append"
                      onClick={applySearchNow}>
                      <span className="input-group-text">
                        <i className="flaticon-381-search-2" />
                      </span>
                    </div>
                  </div>
                  <div className="input-group search-area rounded d-inline-flex mr-sm-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="雇用期間満了日検索"
                      value={query_employmentEndAt}
                      onChange={(e) => setQuery_employmentEndAt(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          applySearchNow();
                        }
                      }}
                    />
                    <div
                      className="input-group-append"
                      onClick={applySearchNow}>
                      <span className="input-group-text">
                        <i className="flaticon-381-search-2" />
                      </span>
                    </div>
                  </div>
                  <div className="input-group search-area rounded d-inline-flex  mr-sm-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="キーワード検索"
                      value={globalFilter}
                      onInput={onSearchChange}
                    />
                    <div
                      className="input-group-append"
                      onClick={applySearchNow}>
                      <span className="input-group-text">
                        <i className="flaticon-381-search-2" />
                      </span>
                    </div>
                  </div>
                  <div className="input-group search-area rounded d-inline-flex ">
                    <Button
                      className="btn btn-light rounded btn-md"
                      onClick={() => handleReset()}>
                      フィルターリセット
                    </Button>
                  </div>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {hasLoadError && (
                <div className="alert alert-warning" role="alert">
                  <span>データを読み込めませんでした。接続を確認して再試行してください。</span>
                  <Button
                    className="btn btn-warning btn-sm ml-3"
                    type="button"
                    disabled={isLoading}
                    onClick={() => setReloadCount((count) => count + 1)}>
                    再試行
                  </Button>
                </div>
              )}
              <div className="tableFixHead2">
                <DataTable
                  value={result.items}
                  loading={isLoading}
                  lazy
                  paginator
                  rows={PAGE_SIZE}
                  first={first}
                  totalRecords={result.total}
                  sortField={sort.field}
                  sortOrder={sort.order === "ASC" ? 1 : -1}
                  onPage={(event) => setPagePosition({
                    criteriaKey, page: Math.floor(event.first / PAGE_SIZE) + 1,
                  })}
                  onSort={(event) => setSort({
                    field: event.sortField || "userNumber",
                    order: event.sortOrder === 1 ? "ASC" : "DESC",
                  })}
                  selectionMode="single"
                  dataKey="id"
                  onRowClick={(e) => handleButtonClick(e.data.userNumber)}>
                  <Column field="userNumber" header="隊員番号" sortable />
                  <Column field="userLastNumber" header="BNo." sortable />
                  <Column field="userName" header="名前" sortable />
                  <Column field="sex" header="性別" sortable />
                  <Column field="belongTo" header="所属" sortable />
                  <Column
                    field="userGroup"
                    header="グループ"
                    sortable
                    body={(rowData) => {
                      return (
                        <span className="ellipsis">
                          {rowData.userGroup
                            ? rowData.userGroup
                                .split(",")
                                .filter((value) => value.length > 1)
                                .join(" / ")
                            : ""}
                        </span>
                      );
                    }}
                  />
                  <Column field="insuranceType" header="保険区分" sortable />
                  <Column field="workerType" header="社員区分" sortable />
                  <Column field="workScope" header="業務区分" sortable />
                  <Column
                    field="employmentEndAt"
                    header="雇用期間"
                    sortable
                    body={(rowData) => "~" + rowData.employmentEndAt}
                  />
                  <Column field="enrollmentType" header="在籍区分" sortable />
                  <Column
                    header="メッセージ"
                    body={(rowData) => {
                      return (
                        <i
                          className="fa fa-regular fa-envelope font-18 align-middle mx-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleButtonClick2(rowData.userNumber);
                          }}></i>
                      );
                    }}
                  />
                </DataTable>
              </div>
              <div className="dataTables_wrapper">
                <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                  <div className="dataTables_info">
                    {startIndex} ～ {endIndex} 件表示 （総{result.total} 件）
                  </div>

                </div>
              </div>
              <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                <div
                  style={
                    admin === "管理者C" || admin === "管理者B"
                      ? { display: "none" }
                      : { display: "block" }
                  }>
                  <Button
                    className="btn btn-primary rounded btn-sm"
                    onClick={() => handleCsv(csv)}>
                    Excelダウンロード
                  </Button>
                </div>
                <div
                  style={
                    admin == "管理者C"
                      ? { display: "none" }
                      : { display: "block" }
                  }>
                  <Link to="/shinryu-adduser">
                    <Button
                      className="btn btn-primary rounded btn-sm"
                      type="submit">
                      新規登録
                    </Button>
                  </Link>
                </div>
              </div>
            </Card.Body>
          </Card>
          {/* <!-- /# card --> */}
        </Col>
      </Row>
    </>
  );
};
export default UserList;
